import { Injectable } from '@angular/core';
import { IUser } from '@app/core/interfaces';
import { ApiService } from '@app/core/services';
import { catchError } from 'rxjs/operators';
import { TokenService } from './token.service';
import * as _ from 'lodash';
import {
    AUTH_TOKEN,
    AUTH_USER,
    EXCHANGE_LOGIN_ROUTE,
    EXCHANGE_ONLINE_AUTO_DISCOVERY_ROUTE,
    LOGIN_ROUTE,
    TOKEN_ROUTE
} from '../../interfaces/app-data.model';
import { environment } from 'projects/extensions/src/environments/environment';
import { AuthType, IAuthenticator } from '@ext-interfaces/authenticator.model';
import { UserService } from '../../services/user.service';

declare const Office: any;
declare var $: any;

@Injectable({
    providedIn: 'root'
})
export class MicrosoftAuthenticatorService implements IAuthenticator {
    private loginInterval: any;
    public isAuthenticating = false;

    constructor(private apiService: ApiService, private tokenService: TokenService, private userService: UserService) {}

    ngOnInit() {}

    authenicateGoogleUser(): void {
        throw new Error('Method not implemented, check if ENV variables are properly set.');
    }

    authenicateMicrosoftUser(authType: AuthType, data?: { email: string; password?: string }): void {
        switch (authType) {
            case 'outlook':
                this.outlook();
                break;
            case 'exchange-online':
                this.exchangeOnline(data?.email);
                break;
            case 'exchange-legacy':
                this.exchangeLegacy(data?.email, data?.password);
                break;
            default:
                break;
        }
    }

    private outlook() {
        const params = this.getAuthParams('noemail');

        this.apiService
            .post(LOGIN_ROUTE, params)
            .pipe(
                catchError((err) => {
                    this.isAuthenticating = false;
                    this.showToast('error', `An error occurred while authenticating through Outlook: ${err}`);

                    return err;
                })
            )
            .subscribe((res?: { url: string }) => {
                this.loginInterval = setInterval(() => this.continueAuthLoop(params), 2000);

                if (environment.localDevData) {
                    window.open(res.url);
                } else {
                    Office.context.ui.displayDialogAsync(res.url);
                }
            });
    }

    private getAuthParams(permissions?: 'noemail') {
        // If we're in local dev mode, use the supplied display name and email instead of relying on Office library for context
        if (environment.localDevData) {
            const { displayName, email } = environment.localDevData;

            return {
                permissions: permissions,
                user: {
                    name: displayName,
                    messaging: [
                        {
                            id: email,
                            meta: {
                                token: this.tokenService.getToken()
                            },
                            type: 'outlook'
                        }
                    ]
                },
                loginType: 'Outlook'
            };
        } else {
            const profile = Office.context.mailbox.userProfile;
            return {
                permissions: permissions,
                user: {
                    name: profile.displayName,
                    messaging: [
                        {
                            id: profile.emailAddress,
                            meta: {
                                token: this.tokenService.getToken()
                            },
                            type: 'outlook'
                        }
                    ]
                },
                loginType: 'Outlook'
            };
        }
    }
    private async continueAuthLoop(params?: any) {
        const profile = params.user?.messaging?.[0].meta?.token || this.tokenService.getToken();

        this.apiService.post(TOKEN_ROUTE, { email: profile }).subscribe((user?: IUser) => {
            if (user) {
                this.completeAuthentication(user);
                clearInterval(this.loginInterval);
                this.userService.checkAndSetIfAuthenticated();
                this.isAuthenticating = true;
            }
        });
    }

    private completeAuthentication(user: IUser) {
        this.tokenService.saveTokenInLocalStorage(AUTH_TOKEN, user.token);
        this.tokenService.saveTokenInLocalStorage(AUTH_USER, JSON.stringify(user));
    }

    private exchangeOnline(email?: string) {
        this.apiService
            .get(`${EXCHANGE_ONLINE_AUTO_DISCOVERY_ROUTE}?email=${encodeURIComponent(email)}`, {
                responseType: 'text'
            })
            .pipe(
                catchError((err) => {
                    this.showToast('error', `An error occurred while authenticating through exchange online 1: ${err}`);
                    this.isAuthenticating = false;
                    return err;
                })
            )
            .subscribe((domain?: string) => {
                var _domain = domain || 'common';

                _domain = _domain.replace('https://', '').replace('http://', '');
                if (_.startsWith(_domain, 'www.')) {
                    _domain = _domain.replace('www.', '');
                }
                if (_.endsWith(_domain, '/')) {
                    _domain = _domain.substr(0, domain.length - 1);
                }

                const params = this.getAuthParams('noemail');

                this.apiService
                    .post(LOGIN_ROUTE, { ...params, _domain })
                    .pipe(
                        catchError((err) => {
                            this.showToast(
                                'error',
                                `An error occurred while authenticating through exchange online 2: ${err}`
                            );
                            this.isAuthenticating = false;
                            return err;
                        })
                    )
                    .subscribe((res?: { url: string }) => {
                        this.loginInterval = setInterval(() => this.continueAuthLoop(params), 2000);

                        if (environment.localDevData) {
                            window.open(res.url);
                        } else {
                            Office.context.ui.displayDialogAsync(res.url);
                        }
                    });
            });
    }

    private exchangeLegacy(email?: string, password?: string) {
        // this.authenticating.next(true);
        const params = this.getAuthParams('noemail');

        this.apiService
            .post(EXCHANGE_LOGIN_ROUTE, { e: email, p: password, user: params.user })
            .pipe(
                catchError((err) => {
                    this.isAuthenticating = false;
                    this.showToast('error', `An error occurred while authenticating through exchange on-prem: ${err}`);
                    return err;
                })
            )
            .subscribe(() => {
                this.loginInterval = setInterval(() => this.continueAuthLoop(params), 2000);
            });
    }

    private showToast(type: 'error' | 'success', message: string) {
        $('body').toast({
            class: type,
            message
        });
    }
}
